import React, { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

/* FAQ Data (unchanged) */

const faqItems = [
  {
    question: "What programs does Akash Institute of Pharmacy offer?",
    answer:
      "Akash Institute of Pharmacy currently offers the Bachelor of Pharmacy (B.Pharm).",
  },
  {
    question: "What are the eligibility criteria for the B.Pharm program?",
    answer:
      "Candidates must have passed 10+2 with Physics and Chemistry as compulsory subjects.",
  },
];

/* Variants (unchanged) */

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.85 },
  },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.85, delay: 0.15 },
  },
};

const formFieldVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const fieldVariant = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

function Frequently() {
  const [activeIndex, setActiveIndex] = useState(null);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const formRef = useRef(null);
  const submitRef = useRef(null);

  const { ref: leftRef, inView: leftInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const { ref: rightRef, inView: rightInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const handleToggle = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  /* Button animation */

  const handleBtnEnter = () => {
    gsap.to(submitRef.current, {
      scale: 1.06,
      duration: 0.3,
    });
  };

  const handleBtnLeave = () => {
    gsap.to(submitRef.current, {
      scale: 1,
      duration: 0.3,
    });
  };

  /* EMAILJS SUBMIT */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setSuccess(false);

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setSuccess(true);
      formRef.current.reset();
    } catch (error) {
      console.error(error);
      alert("Failed to send enquiry. Please try again.");
    }

    setLoading(false);
  };

  return (
    <section className="faq-area pt-120 pb-120 p-relative fix">
      <div className="container">
        <div className="row justify-content-center align-items-center">

          {/* LEFT FAQ */}

          <div className="col-lg-7" ref={leftRef}>
            <motion.div
              className="section-title"
              variants={fadeInLeft}
              initial="hidden"
              animate={leftInView ? "visible" : "hidden"}
            >
              <h2>Frequently Asked Questions</h2>
            </motion.div>
          </div>

          {/* RIGHT FORM */}

          <div className="col-lg-5" ref={rightRef}>
            <motion.div
              className="contact-bg02"
              variants={fadeInRight}
              initial="hidden"
              animate={rightInView ? "visible" : "hidden"}
            >
              <motion.div className="section-title">
                <h2>Make An Contact</h2>
              </motion.div>

              <motion.form
                ref={formRef}
                onSubmit={handleSubmit}
                className="contact-form mt-30"
                variants={formFieldVariants}
                initial="hidden"
                animate={rightInView ? "visible" : "hidden"}
              >
                <div className="row">

                  {/* Name */}

                  <motion.div
                    className="col-lg-12"
                    variants={fieldVariant}
                  >
                    <div className="contact-field mb-20">
                      <input
                        type="text"
                        name="user_name"
                        placeholder="Full Name"
                        required
                      />
                    </div>
                  </motion.div>

                  {/* Email */}

                  <motion.div
                    className="col-lg-12"
                    variants={fieldVariant}
                  >
                    <div className="contact-field mb-20">
                      <input
                        type="email"
                        name="user_email"
                        placeholder="Email Address"
                        required
                      />
                    </div>
                  </motion.div>

                  {/* Phone */}

                  <motion.div
                    className="col-lg-12"
                    variants={fieldVariant}
                  >
                    <div className="contact-field mb-20">
                      <input
                        type="text"
                        name="user_phone"
                        placeholder="Phone Number"
                        required
                      />
                    </div>
                  </motion.div>

                  {/* Course */}

                  <motion.div
                    className="col-lg-12"
                    variants={fieldVariant}
                  >
                    <div className="contact-field mb-20">
                      <select
                        name="course"
                        required
                      >
                        <option value="">
                          Select Course
                        </option>

                        <option value="Bachelor of Pharmacy (B.Pharm)">
                          Bachelor of Pharmacy (B.Pharm)
                        </option>
                      </select>
                    </div>
                  </motion.div>

                  {/* Message */}

                  <motion.div
                    className="col-lg-12"
                    variants={fieldVariant}
                  >
                    <div className="contact-field mb-30">
                      <textarea
                        name="message"
                        rows="6"
                        placeholder="Please share your enquiry, admission interest, or any questions about the B.Pharm program."
                      />
                    </div>

                    {/* Hidden Website */}

                    <input
                      type="hidden"
                      name="website"
                      value="Akash Institute of Pharmacy"
                    />

                    <motion.div className="slider-btn">
                      <button
                        ref={submitRef}
                        className="btn ss-btn"
                        type="submit"
                        disabled={loading}
                        onMouseEnter={handleBtnEnter}
                        onMouseLeave={handleBtnLeave}
                      >
                        {loading
                          ? "Sending..."
                          : "Submit Now"}
                      </button>
                    </motion.div>

                    {success && (
                      <p
                        style={{
                          color: "green",
                          marginTop: "12px",
                          fontWeight: 600,
                        }}
                      >
                        Thank you! Your enquiry has been submitted successfully.
                      </p>
                    )}
                  </motion.div>

                </div>
              </motion.form>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}

export default Frequently;